import type { AcknowledgementRecordDto } from "@/generated/model/acknowledgementRecordDto";
import { AcknowledgementStanding } from "@/generated/model/acknowledgementStanding";
import type { EmployeeDirectoryAcknowledgementsDto } from "@/generated/model/employeeDirectoryAcknowledgementsDto";
import type { MyAcknowledgementRequestDto } from "@/generated/model/myAcknowledgementRequestDto";
import type { StaffAcknowledgementsDto } from "@/generated/model/staffAcknowledgementsDto";
import { ACK_COPY } from "@/lib/acknowledgements/copy";
import { itemTypeMeta } from "@/lib/acknowledgements/item-types";
import { formatUkDate } from "@/lib/date-utils";
import { formatProfileTimestamp } from "@/lib/people";

export const PROFILE_ACKNOWLEDGEMENTS_PAGE_SIZE = 10;

export const NOT_READ_YET = "Not read yet";

/**
 * True for the 403 and 404 the acknowledgement API returns to a caller who may
 * not see this person's records. The tab then says so rather than showing
 * nothing.
 */
export function isAcknowledgementsWithheld(error: unknown): boolean {
  const e = error as { response?: { status?: number }; status?: number };
  const status = e?.response?.status ?? e?.status;
  return status === 403 || status === 404;
}

/** `completed` is only the current page, so the total decides. */
export function hasNoAcknowledgements(view: StaffAcknowledgementsDto): boolean {
  return view.outstanding.length === 0 && view.completedTotal === 0;
}

export function itemSummary(itemType: string, version: number): string {
  const { singular } = itemTypeMeta(itemType);
  return `${singular.charAt(0).toUpperCase()}${singular.slice(1)} · Version ${version}`;
}

export function outstandingDates(request: MyAcknowledgementRequestDto): {
  sent: string;
  due: string;
  read: string;
} {
  return {
    sent: formatProfileTimestamp(request.requestedAt),
    due: request.dueAt
      ? formatUkDate(new Date(request.dueAt), "d MMM yyyy")
      : ACK_COPY.ROW_NO_DUE_DATE,
    read: request.readAt
      ? formatProfileTimestamp(request.readAt)
      : NOT_READ_YET,
  };
}

/** Records migrated from before NAU-410 have no read date, and show Not recorded. */
export function completedDates(record: AcknowledgementRecordDto): {
  read: string;
  signed: string;
} {
  return {
    read: formatProfileTimestamp(record.readAt),
    signed: formatProfileTimestamp(record.acknowledgedAt),
  };
}

function documents(count: number): string {
  return count === 1 ? "1 document" : `${count} documents`;
}

/** `name` is who the profile belongs to; on your own profile it speaks to you. */
export function acknowledgementSummaryText(
  summary: EmployeeDirectoryAcknowledgementsDto,
  name: string,
  isOwnProfile: boolean,
): string {
  if (summary.outstandingCount > 0) {
    const waiting = `${documents(summary.outstandingCount)} to read and sign`;
    if (summary.overdueCount === 0) return `${waiting}.`;
    return `${waiting}, ${summary.overdueCount} of them overdue.`;
  }
  if (summary.standing === AcknowledgementStanding.UP_TO_DATE) {
    return isOwnProfile
      ? "You have signed everything sent so far."
      : `${name} has signed everything sent so far.`;
  }
  return isOwnProfile
    ? "Nothing has been sent to you yet."
    : `Nothing has been sent to ${name} yet.`;
}

/** Only the person themselves can act on what is outstanding; anyone else views. */
export function acknowledgementSummaryAction(
  summary: EmployeeDirectoryAcknowledgementsDto,
  isOwnProfile: boolean,
): string {
  if (!isOwnProfile) return "View";
  if (summary.outstandingCount === 0) return "View";
  return "Read and confirm";
}
